import { useCallback, useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import { VALET_PAGE_POLL_INTERVAL_MS } from '@hopdrive/feature-order-index';
import { useViewAssignmentsQuery } from '../queries/viewAssignments.query.generated';
import { buildViewAssignmentsReport, type ViewAssignmentsReport } from '../utils/viewAssignmentsRows';

/**
 * The move cap — the same backstop the dashboard index uses. The query asks for CAP + 1
 * so the extra row is the truncation probe (see the report's `truncated` flag).
 */
export const VIEW_ASSIGNMENTS_MOVE_CAP = 2000;

export interface UseViewAssignmentsReportResult {
  report: ViewAssignmentsReport;
  loading: boolean;
  /**
   * True only while there is nothing to show yet. `loading` is also true on every
   * 60-second background poll (notifyOnNetworkStatusChange), so anything that swaps to a
   * loading state on `loading` flashes each minute; the KPI tiles read this instead.
   */
  initialLoading: boolean;
  /** Apollo's own error type — what the underlying query hands back, not a bare Error. */
  error?: ApolloError;
  /**
   * Re-run the report now, outside the 60-second poll. The page calls it when the assign
   * dialog closes so the row reflects an assign/unassign immediately.
   */
  refetch: () => void;
}

/**
 * Fetches the windowed offer report and classifies it with the shared derivation. The
 * window (`start`/`end`) must be resolved by the caller with `resolveEffectiveDateWindow`,
 * the SAME resolver the board uses, so the two surfaces define "in the window" one way.
 */
export const useViewAssignmentsReport = ({
  customerId,
  rooftopTier,
  start,
  end,
}: {
  customerId: number;
  rooftopTier: string | null;
  start: string;
  end: string;
}): UseViewAssignmentsReportResult => {
  const { data, loading, error, refetch } = useViewAssignmentsQuery({
    variables: { customerId, start, end, limit: VIEW_ASSIGNMENTS_MOVE_CAP + 1 },
    fetchPolicy: 'cache-and-network',
    skip: !customerId,
    // The report watches a LIVE lifecycle — an accept should appear without a refresh.
    // Same poll cadence as the scenario board (there is no subscription on either
    // surface; offers don't touch moves.updatedat, which is also why the server bumps
    // it on offered). The page already renders poll-safely: the spinner shows only
    // when there are no rows yet, so a background poll never blanks the table.
    pollInterval: VALET_PAGE_POLL_INTERVAL_MS,
    // Without this, Apollo suppresses re-render on poll results that only change
    // nested rows; with it, `loading` flickers are already handled by the page's
    // rows-present check.
    notifyOnNetworkStatusChange: true,
  });

  const report = useMemo(
    () => buildViewAssignmentsReport(data, { rooftopTier, cap: VIEW_ASSIGNMENTS_MOVE_CAP }),
    [data, rooftopTier]
  );

  const refetchReport = useCallback(() => {
    // Fire-and-forget: a failed refetch surfaces through `error` like a failed poll, and
    // must not become an unhandled rejection from a dialog's close handler.
    refetch?.()?.catch?.(() => undefined);
  }, [refetch]);

  return { report, loading, initialLoading: loading && !data, error, refetch: refetchReport };
};
